import { useMemo } from "react";
import { AlertTriangle, CheckCircle, FileText } from "lucide-react";
import { PaneHeader } from "@features/pane-manager/PaneHeader";
import { usePaneManager } from "@features/pane-manager/store";
import { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import { navigateToTarget, useDeck } from "@features/office";
import { createTargetNameResolver, useElements, useSlides } from "@entities/generation-setting";
import { Button } from "@shared/ui/Button";
import { notify } from "@shared/ui/toast/toastStore";
import { generationSettingStore } from "@entities/generation-setting";
import {
    invalidateGenerationResult,
    invalidateOutcome,
    useGenerationResult,
    useOutcome,
} from "@entities/generation-task";
import type { OutputResult, Review } from "@entities/generation-task";

import { OutcomeResults } from "./OutcomeResults";

function OutputRow({
    output,
    label,
    onOpenPrompt,
}: {
    output: OutputResult;
    label: string;
    onOpenPrompt: () => void;
}) {
    function handleNavigate() {
        void navigateToTarget(output.targetRef).then((r) => {
            if (r === "not-found") notify("対象の要素が見つかりません", "error");
        });
    }

    return (
        <div className="rounded border border-border-subtle bg-surface p-3">
            <div className="mb-1.5 flex items-center gap-2">
                <button
                    className="shrink-0 rounded bg-sidebar-bg px-1.5 py-0.5 text-[11px] font-semibold text-text-muted transition-colors hover:bg-brand/10 hover:text-brand"
                    onClick={handleNavigate}
                    title="PowerPoint で表示"
                >
                    {output.targetRef.startsWith("slide:") ? "スライド" : "図形"}
                </button>
                <button
                    className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-interactive transition-colors hover:text-interactive-active hover:underline"
                    onClick={onOpenPrompt}
                    title="プロンプト設定を開く"
                >
                    {label || output.targetRef}
                </button>
            </div>

            {output.provenance.length > 0 && (
                <div className="mt-1.5 space-y-1">
                    <p className="text-[11px] font-semibold text-text-muted">根拠</p>
                    {output.provenance.map((p, i) => (
                        <div key={i} className="rounded bg-sidebar-bg p-1.5">
                            <span className="flex items-center gap-1 text-[11px] text-interactive">
                                <FileText size={10} />
                                {p.sourceRef}
                            </span>
                            {p.excerpt && (
                                <p className="mt-0.5 text-[11px] leading-relaxed text-text-muted">
                                    {p.excerpt}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReviewRow({ review }: { review: Review }) {
    const isWarning = review.level === "warning" || review.level === "error";
    return (
        <div
            className={`flex items-start gap-2 rounded p-2 text-[12px] ${isWarning ? "bg-warning/10 text-warning" : "bg-sidebar-bg text-text-muted"}`}
        >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
                <p>{review.message}</p>
                {review.sourceRefs && review.sourceRefs.length > 0 && (
                    <p className="mt-0.5 text-[11px] opacity-80">
                        ソース: {review.sourceRefs.join(", ")}
                    </p>
                )}
            </div>
        </div>
    );
}

export function GenerationResultsPan() {
    const openPane = usePaneManager((s) => s.openPane);
    // 戻ってきたら出ていった行へ帰す（[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
    const scroll = useScrollMemory();
    const nameOf = useTargetName();

    const sourceTaskId = generationSettingStore.getSourceTaskId();
    const sourceProjectId = generationSettingStore.getSourceProjectId();
    const { status, result } = useGenerationResult(sourceTaskId, sourceProjectId);
    const outcomeEntry = useOutcome(sourceTaskId, sourceProjectId);

    /** 結果を取り直す。取ったものを捨てると、次の照会でサーバーへ行く。 */
    function reload(): void {
        invalidateGenerationResult(sourceTaskId, sourceProjectId);
        invalidateOutcome(sourceTaskId, sourceProjectId);
    }

    if (!sourceTaskId || !sourceProjectId) {
        return (
            <>
                <PaneHeader title="生成結果" />
                <div className="flex flex-1 items-center justify-center px-4">
                    <p className="text-center text-sm text-text-muted">
                        生成結果がありません。Web コンソールから内部用 PPTX
                        をダウンロードすると、結果が表示されます。
                    </p>
                </div>
            </>
        );
    }

    if (status === "loading") {
        return (
            <>
                <PaneHeader title="生成結果" />
                <div className="flex flex-1 items-center justify-center">
                    <p className="text-sm text-text-muted">読み込み中...</p>
                </div>
            </>
        );
    }

    // **「取れなかった」と「まだ無い」を分ける。** 以前は同じ枝で「取得に失敗しました。
    // ネットワーク接続を確認してください」と出していたため、生成がまだ終わっていない
    // タスクを開いた利用者が通信を疑って無駄な確認をしていた（記録 22）。
    if (status === "error" || !result) {
        return (
            <>
                <PaneHeader title="生成結果" />
                <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4">
                    <p className="text-center text-sm text-text-muted">
                        {status === "error"
                            ? "生成結果の取得に失敗しました。ネットワーク接続を確認してください。"
                            : "まだ結果がありません。生成中か、生成が失敗しています。"}
                    </p>
                    {/* 一度取ったら取り直す口が無かったので、生成が完了しても表示が変わらなかった
                        （記録 22）。押したときに取り直す（アドインに通知の仕組みは無い）。 */}
                    <Button variant="secondary" onClick={reload}>
                        読み直す
                    </Button>
                </div>
            </>
        );
    }

    // **結果情報があれば新しい一覧、無ければ現行の一覧。** この器より前に完了したタスクは結果
    // 情報を持たず、報告の段を後から呼べないので作り直せない。落ちる先を用意しないと、過去の
    // 生成の結果が画面から消える（[[2026-09-10 設計ノート アドイン 生成結果画面の結果情報対応]]）。
    if (outcomeEntry?.status === "loaded" && outcomeEntry.outcome) {
        return (
            <>
                <PaneHeader title="生成結果" />
                <OutcomeResults
                    outcome={outcomeEntry.outcome}
                    reviewed={outcomeEntry.reviewed}
                    taskId={sourceTaskId}
                    projectId={sourceProjectId}
                    scroll={scroll}
                />
            </>
        );
    }

    const reviews = result.reviews ?? [];

    return (
        <>
            <PaneHeader title="生成結果" />
            <div ref={scroll.ref} className="flex-1 overflow-y-auto">
                <div className="px-4 pt-3 pb-2">
                    <div className="flex items-center gap-2 text-xs text-text-muted">
                        <CheckCircle size={14} className="text-success" />
                        {result.outputs.length} 件の出力
                        {reviews.length > 0 && (
                            <span className="ml-2 flex items-center gap-1 text-warning">
                                <AlertTriangle size={12} />
                                {reviews.length} 件の確認事項
                            </span>
                        )}
                    </div>
                </div>

                {result.outputs.length > 0 && (
                    <div className="space-y-2 px-4 pb-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                            出力ターゲット
                        </p>
                        {result.outputs.map((output, i) => (
                            // 出所の目印は行の外側に置く（OutputRow は素の props しか受けない）。
                            <div key={`${output.targetRef}-${i}`} data-anchor={output.promptId}>
                                <OutputRow
                                    output={output}
                                    label={nameOf(output.targetRef)}
                                    onOpenPrompt={() => {
                                        if (!output.promptId) return;
                                        scroll.leaveFrom(output.promptId);
                                        openPane("assignment", output.promptId);
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {reviews.length > 0 && (
                    <div className="space-y-1.5 px-4 pb-4">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                            要確認事項
                        </p>
                        {reviews.map((r, i) => (
                            <ReviewRow key={`${r.code}-${i}`} review={r} />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

/**
 * 対象の参照から表示名を引く関数を作る。
 *
 * **枚も引ける表を使う。** 以前はここで図形だけの対応表を組んでいたため、`slide:` 宛ての
 * 出力が生の参照で並んでいた（記録 69）。表の作り方は画面C と同じ関数へ寄せてあり、
 * 旧鍵（`a16:creationId`）での引き当て（記録 15）もそちらが持つ。
 */
function useTargetName(): (ref: string) => string {
    const deck = useDeck();
    const slides = useSlides();
    const elements = useElements();
    return useMemo(
        () => createTargetNameResolver({ deck: deck.slides, slides, elements }),
        [deck, slides, elements]
    );
}
